import uiI18n from "../i18n";
import { apiRequest, jsonOptions } from "./apiClient";
import { waitMinutes } from "../utils/retryAfter";

const BASE = "property-requests";

function query(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== "") search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : "";
}

// API error codes of the proposal operations that need their own translated message.
const CODES = {
  duplicate_proposal: "alreadyProposed", request_not_active: "requestClosed", own_request: "ownRequest",
  listing_not_owned: "listingNotOwned", listing_not_found: "listingNotFound", listing_transaction_mismatch: "listingMismatch",
  too_many_proposals: "tooManyProposals", invalid_message: "messageInvalid",
};

// Lists (public list, "Mes recherches"): a 404 is not a missing request but an unavailable service.
const LIST_OPERATIONS = ["list", "mine"];

// Translated message for each API status of an operation (the server's French text is not shown).
export function propertyRequestError(error, operation) {
  const status = error?.status;
  const proposal = ["candidates", "propose", "proposals", "withdraw"].includes(operation);
  const key = proposal && CODES[error?.code] ? CODES[error.code]
    : status === 0 ? null
    : status === 400 ? "invalid"
    : status === 401 ? "signIn"
    : status === 403 ? "forbidden"
    : status === 404 ? (LIST_OPERATIONS.includes(operation) ? "server" : "notFound")
    : status === 409 ? (operation === "create" ? "tooManyActive" : "notActive")
    : status === 429 ? (operation === "create" ? "tooManyCreated" : error?.retryAfter ? "tooManyRequestsWait" : "tooManyRequests")
    : "server";
  // With a known wait (Retry-After), the message gives it in minutes, rounded up.
  const options = key === "tooManyRequestsWait" ? { count: waitMinutes(error.retryAfter) } : undefined;
  const result = new Error(key ? uiI18n.t(`propertyRequest.errors.${key}`, options) : error?.message || uiI18n.t("propertyRequest.errors.server"));
  result.status = status;
  if (error?.code) result.code = error.code;
  if (status === 429 && error?.retryAfter) result.retryAfter = error.retryAfter;
  return result;
}

async function call(operation, path, options) {
  try {
    return await apiRequest(path, options);
  } catch (error) {
    throw propertyRequestError(error, operation);
  }
}

export const propertyRequestApi = {
  list: (filters = {}, page = 0, pageSize = 20) =>
    call("list", `${BASE}${query({ ...filters, page, pageSize })}`, { authenticated: false }),
  get: (id) => call("get", `${BASE}/${encodeURIComponent(id)}`, { authenticated: false }),
  mine: (status, page = 0, pageSize = 20) => call("mine", `${BASE}/mine${query({ status, page, pageSize })}`),
  create: (payload) => call("create", BASE, jsonOptions("POST", payload)),
  update: (id, payload) => call("update", `${BASE}/${encodeURIComponent(id)}`, jsonOptions("PUT", payload)),
  close: (id) => call("close", `${BASE}/${encodeURIComponent(id)}/close`, { method: "POST" }),
  remove: (id) => call("remove", `${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" }),
  // Visitors may analyse and match: no token is sent, nothing is stored.
  analyze: (text, manual, language) =>
    call("analyze", `${BASE}/analyze`, { ...jsonOptions("POST", { text, manual, language }), authenticated: false }),
  matches: (criteria) => call("matches", `${BASE}/matches`, { ...jsonOptions("POST", criteria), authenticated: false }),
  // "Proposer un bien": the proposer is the signed-in user (token); only kind, listing and message are sent.
  candidates: (id) => call("candidates", `${BASE}/${encodeURIComponent(id)}/proposal-candidates`),
  propose: (id, { listingKind, listingId, message }) =>
    call("propose", `${BASE}/${encodeURIComponent(id)}/proposals`, jsonOptions("POST", { listingKind, listingId, message: message?.trim() || null })),
  proposals: (id) => call("proposals", `${BASE}/${encodeURIComponent(id)}/proposals`),
  withdraw: (proposalId) => call("withdraw", `${BASE}/proposals/${encodeURIComponent(proposalId)}/withdraw`, { method: "POST" }),
};
